"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.carePlanUpdatedPayloadSchema = exports.clinicalTaskCompletedPayloadSchema = exports.orderCancelledPayloadSchema = exports.referralCreatedPayloadSchema = exports.nursingOrderCreatedPayloadSchema = exports.procedureOrderedPayloadSchema = exports.radiologyOrderedPayloadSchema = exports.labOrderedPayloadSchema = exports.medicationOrderedPayloadSchema = exports.clinicalDocumentAmendedPayloadSchema = exports.clinicalDocumentFinalizedPayloadSchema = exports.clinicalDocumentCreatedPayloadSchema = exports.encounterCancelledPayloadSchema = exports.encounterClosedPayloadSchema = exports.encounterCompletedPayloadSchema = exports.encounterResumedPayloadSchema = exports.encounterSuspendedPayloadSchema = exports.encounterStartedPayloadSchema = exports.ClinicalEventNames = void 0;
/**
 * Domain event names + payload schemas for the Clinical / EMR bounded
 * context (Stage 6C.2). Mirrors `financial.events.ts`'s shape exactly:
 * published by Clinical Domain Services through the enveloped Domain
 * Event Layer (`modules/clinical/_shared-event`, itself a same-shape
 * sibling of `modules/financial/_shared-event`, not a shared runtime
 * instance — ADR-029, Stage 6A). Only events with a genuine downstream
 * business purpose are defined here — no speculative "just in case"
 * events, with one disclosed exception: `CARE_PLAN_UPDATED` is defined
 * (named explicitly by Stage 6C.2's own brief, STEP 8) but has no
 * Domain Service publishing it yet — `ClinicalCarePlanRepository`
 * itself was deliberately not built in Stage 6C.1
 * (`CLINICAL-REPOSITORY-REUSE.md` §6), so there is no Care Plan
 * operation this stage can wire it to. Defined for forward
 * compatibility with whichever future stage builds that repository,
 * not silently omitted.
 */
const zod_1 = require("zod");
exports.ClinicalEventNames = {
    ENCOUNTER_STARTED: 'clinical.encounter-started',
    ENCOUNTER_SUSPENDED: 'clinical.encounter-suspended',
    ENCOUNTER_RESUMED: 'clinical.encounter-resumed',
    ENCOUNTER_COMPLETED: 'clinical.encounter-completed',
    ENCOUNTER_CLOSED: 'clinical.encounter-closed',
    ENCOUNTER_CANCELLED: 'clinical.encounter-cancelled',
    CLINICAL_DOCUMENT_CREATED: 'clinical.document-created',
    CLINICAL_DOCUMENT_FINALIZED: 'clinical.document-finalized',
    CLINICAL_DOCUMENT_AMENDED: 'clinical.document-amended',
    MEDICATION_ORDERED: 'clinical.medication-ordered',
    LAB_ORDERED: 'clinical.lab-ordered',
    RADIOLOGY_ORDERED: 'clinical.radiology-ordered',
    PROCEDURE_ORDERED: 'clinical.procedure-ordered',
    NURSING_ORDER_CREATED: 'clinical.nursing-order-created',
    REFERRAL_CREATED: 'clinical.referral-created',
    ORDER_CANCELLED: 'clinical.order-cancelled',
    CLINICAL_TASK_COMPLETED: 'clinical.task-completed',
    CARE_PLAN_UPDATED: 'clinical.care-plan-updated',
};
exports.encounterStartedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    visitId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid(),
});
exports.encounterSuspendedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    reason: zod_1.z.string().nullable(),
});
exports.encounterResumedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
});
exports.encounterCompletedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid(),
});
exports.encounterClosedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid(),
    closureReasonCodeId: zod_1.z.string().uuid().nullable(),
});
exports.encounterCancelledPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    reason: zod_1.z.string().nullable(),
});
exports.clinicalDocumentCreatedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    documentId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    documentType: zod_1.z.string(),
});
exports.clinicalDocumentFinalizedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    documentId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    signedBy: zod_1.z.string().uuid(),
    coSignedBy: zod_1.z.string().uuid().nullable(),
});
exports.clinicalDocumentAmendedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    originalDocumentId: zod_1.z.string().uuid(),
    amendmentDocumentId: zod_1.z.string().uuid(),
});
exports.medicationOrderedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    drugMasterId: zod_1.z.string().uuid(),
});
exports.labOrderedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    testMasterId: zod_1.z.string().uuid(),
});
exports.radiologyOrderedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    radiologyProcedureMasterId: zod_1.z.string().uuid(),
});
exports.procedureOrderedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    procedureMasterId: zod_1.z.string().uuid(),
});
exports.nursingOrderCreatedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
});
exports.referralCreatedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
});
exports.orderCancelledPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    orderId: zod_1.z.string().uuid(),
    orderType: zod_1.z.string(),
    reason: zod_1.z.string().nullable(),
});
exports.clinicalTaskCompletedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    taskId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid(),
});
exports.carePlanUpdatedPayloadSchema = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    carePlanId: zod_1.z.string().uuid(),
    patientId: zod_1.z.string().uuid(),
});
//# sourceMappingURL=clinical.events.js.map